import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useRouteContext } from '@tanstack/react-router';
import { CountUp, delay, Empty, formatDay, plain, Select, STAGGER, Tabs, useToast } from '@caios/ui';
import { authClient, type Me } from '../auth.ts';
import { ApproveAllFixes, FixButtons, fixIdOf } from '../components/fix-decision.tsx';

/**
 * Home = the Bridge (live Owner Dashboard), fed by the Inbox (Wave A2): a briefing hero with the next best
 * action, the numbers, "Waiting on you" and "On your plate". Same order and density as live.
 */
type Item = { id: string; kind: string; title: string; body: string | null; link: string; created_at: string; read_at: string | null; snoozed_until: string | null; done_at: string | null };
type Task = { id: string; title: string; category: string; priority: string; due_on: string | null; list: string };
const KIND: Record<string, { label: string; tone: string }> = {
  assigned: { label: 'Assigned', tone: 'decision' }, mentioned: { label: 'Mention', tone: 'decision' },
  due: { label: 'Due today', tone: 'warn' }, approval: { label: 'Approval', tone: 'fire' }, system: { label: 'CAIOS', tone: 'accent' }, ticket: { label: 'Support', tone: 'warn' }, booking: { label: 'Booking', tone: 'good' },
};
const post = (path: string, body: unknown = {}) => fetch(path, { method: 'POST', credentials: 'include', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });
const days = (iso: string) => Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000));
const age = (iso: string) => { const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000); return m < 60 ? `${Math.max(1, m)}m` : m < 1440 ? `${Math.round(m / 60)}h` : `${Math.round(m / 1440)}d`; };
const todayIso = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Madrid' });

export function Home() {
  const { me } = useRouteContext({ from: '/_app' }) as { me: Me };
  const session = authClient.useSession();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const [view, setView] = useState<'open' | 'snoozed' | 'done'>('open');
  const [leaving, setLeaving] = useState<Set<string>>(new Set());
  const q = useQuery({ queryKey: ['inbox', view], queryFn: async () => (await (await fetch(`/api/v1/inbox?view=${view}`, { credentials: 'include' })).json()) as Item[], refetchInterval: 20_000 });
  const openQ = useQuery({ queryKey: ['inbox', 'open'], queryFn: async () => (await (await fetch('/api/v1/inbox?view=open', { credentials: 'include' })).json()) as Item[], refetchInterval: 20_000 });
  const brief = useQuery({ queryKey: ['briefing'], queryFn: async () => { const r = await fetch('/api/v1/briefing', { credentials: 'include' }); return r.ok ? ((await r.json()) as Briefings) : null; }, refetchInterval: 300_000 });
  const tasks = useQuery({ queryKey: ['my-work'], queryFn: async () => { const r = await post('/api/v1/ops/work', {}); return r.ok ? ((await r.json()) as Task[]) : null; } });
  const refresh = () => { qc.invalidateQueries({ queryKey: ['inbox'] }); qc.invalidateQueries({ queryKey: ['inbox-count'] }); };
  const act = useMutation({ mutationFn: (a: { id: string; action: string; body?: unknown }) => post(`/api/v1/inbox/${a.id}/${a.action}`, a.body), onSuccess: refresh });
  const leave = (i: Item, action: string, body?: unknown) => {
    setLeaving((s) => new Set(s).add(i.id));
    toast(action === 'done' ? <>Done: <b>{i.title}</b> · Inbox</> : action === 'snooze' ? <>Snoozed <b>{i.title}</b> · back {(body as { until: string }).until.replace('_', ' ')}</> : <>Reopened <b>{i.title}</b> · Inbox</>);
    setTimeout(() => act.mutate({ id: i.id, action, ...(body ? { body } : {}) }), 400);
  };
  const open = async (i: Item) => { if (!i.read_at) await post(`/api/v1/inbox/${i.id}/read`); refresh(); navigate({ to: i.link.split('?')[0] as '/', search: Object.fromEntries(new URLSearchParams(i.link.split('?')[1] ?? '')) as never }); };

  const first = (session.data?.user.name ?? '').split(' ')[0] || 'Hello';
  const hour = new Date().getHours();
  const part = hour < 12 ? 'morning' : hour < 18 ? 'afternoon' : 'evening';
  const all = openQ.data ?? [];
  const items = q.data ?? [];
  const unread = all.filter((i) => !i.read_at).length;
  const approvals = all.filter((i) => i.kind === 'approval').length;
  const next = all.find((i) => i.kind === 'approval') ?? all.find((i) => !i.read_at) ?? all[0];
  const t = tasks.data ?? [];
  const today = todayIso();
  const overdue = t.filter((x) => x.due_on && x.due_on < today);
  const dueToday = t.filter((x) => x.due_on === today);
  const active = t.filter((x) => x.category === 'active');

  return (
    <div className="cx-stack">
      <section className="cx-hero-panel cx-section cx-rise">
        <div className="cx-form-row" style={{ justifyContent: 'space-between' }}>
          <div className="cx-eyebrow"><b>The Bridge</b> — Inbox · {part} briefing</div>
          <Health role={me.role} />
        </div>
        <h1 className="cx-hero" style={{ margin: '10px 0 12px' }} data-testid="bridge-headline">
          {first} — {all.length ? <>{all.length} thing{all.length === 1 ? '' : 's'} waiting on you{overdue.length ? <>, <em>{overdue.length} overdue task{overdue.length === 1 ? '' : 's'}</em></> : null}.</> : <>you’re clear. <em>Nothing is waiting on you.</em></>}
        </h1>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {approvals > 0 && <span className="cx-pill cx-pill--fire">{approvals} approval{approvals === 1 ? '' : 's'}</span>}
          <span className="cx-pill cx-pill--decision">{unread} unread</span>
          {dueToday.length > 0 && <span className="cx-pill" style={{ ['--c' as string]: 'var(--cx-warn)' }}>{dueToday.length} due today</span>}
        </div>
        {next && (
          <div className="cx-inset cx-next" style={{ marginTop: 14, padding: '12px 14px' }} data-testid="next-action">
            <span className={`cx-chip cx-chip--${KIND[next.kind]?.tone ?? 'accent'}`}>{KIND[next.kind]?.label ?? next.kind}</span>
            <div><div className="cx-label">Next best action</div><button type="button" className="cx-link-btn" style={{ padding: 0, fontSize: 15, fontWeight: 600, color: 'var(--cx-text)', textAlign: 'left' }} onClick={() => open(next)}>{plain(next.title)}</button>
              {next.body && <div className="cx-faint" style={{ fontSize: 12.5, marginTop: 2 }}>{plain(next.body)}</div>}</div>
            {next.kind === 'approval' && fixIdOf(next.link) ? <FixButtons link={next.link} itemId={next.id} onDecided={refresh} />
              : <button type="button" className="cx-btn cx-btn--sm" onClick={() => leave(next, 'snooze', { until: 'tomorrow' })}>Snooze until tomorrow</button>}
          </div>
        )}
      </section>

      <ScottyBrief b={brief.data ?? null} />

      <section style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 14 }}>
        {[
          { k: 'Waiting on you', v: all.length, c: 'var(--cx-data-6)' },
          { k: 'Unread', v: unread, c: 'var(--cx-data-2)' },
          ...(tasks.data ? [
            { k: 'Overdue tasks', v: overdue.length, c: 'var(--cx-fire)' },
            { k: 'Due today', v: dueToday.length, c: 'var(--cx-data-5)' },
            { k: 'In progress', v: active.length, c: 'var(--cx-data-1)' },
          ] : []),
        ].map((s, i) => (
          <div key={s.k} className="cx-stat cx-sheen cx-rise" style={{ ['--c' as string]: s.c, ...delay(i + 1, STAGGER.stats) }}>
            <div className="cx-label">{s.k}</div><div className="cx-value"><CountUp value={s.v} /></div>
          </div>
        ))}
      </section>

      <section className="cx-panel cx-section cx-rise" style={delay(2, STAGGER.tiles)}>
        <div className="cx-form-row" style={{ justifyContent: 'space-between', marginBottom: 4 }}>
          <span className="cx-eyebrow"><b>Waiting on you</b> — every module</span>
          {view === 'open' && <ApproveAllFixes items={all} onDone={refresh} />}
        </div>
        <Tabs value={view} onChange={setView} items={[{ id: 'open', label: 'Open', count: all.length }, { id: 'snoozed', label: 'Snoozed' }, { id: 'done', label: 'Done' }]} testid="inbox-tab" />
        {items.length ? (
          <table className="cx-table">
            <tbody>
              {items.map((i, n) => (
                <tr key={i.id} className={leaving.has(i.id) ? 'cx-leave' : 'cx-rise'} style={delay(n, STAGGER.list)} data-testid="inbox-item">
                  <td className="cx-faint cx-num cx-phone-hide" style={{ width: 28 }}>{n + 1}</td>
                  <td style={{ width: 110 }}><span className={`cx-chip cx-chip--${KIND[i.kind]?.tone ?? 'accent'}`}>{KIND[i.kind]?.label ?? i.kind}</span></td>
                  <td className="cx-lead">
                    <button type="button" className="cx-link-btn" style={{ padding: 0, fontSize: 14, fontWeight: i.read_at ? 500 : 700, color: 'var(--cx-text)', textAlign: 'left' }} onClick={() => open(i)} data-testid="inbox-open">{plain(i.title)}</button>
                    {i.body && <div className="cx-faint" style={{ marginTop: 2 }}>{plain(i.body)}</div>}
                  </td>
                  <td style={{ width: 60, textAlign: 'right' }}><span className={`cx-age${days(i.created_at) > 30 ? ' cx-age--late' : ''}`}>{age(i.created_at)}</span></td>
                  <td className="cx-actions" style={{ width: 230 }}>
                    {view !== 'done' && i.kind === 'approval' && fixIdOf(i.link) && <FixButtons link={i.link} itemId={i.id} onDecided={refresh} />}
                    {view !== 'done' && !(i.kind === 'approval' && fixIdOf(i.link)) && <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
                      <Select aria-label="Snooze" value="" onChange={(e) => e.target.value && leave(i, 'snooze', { until: e.target.value })} data-testid="inbox-snooze" style={{ minHeight: 30, padding: '4px 30px 4px 9px', fontSize: 12.5 }}>
                        <option value="">Snooze…</option><option value="later_today">Later today</option><option value="tomorrow">Tomorrow 8:00</option><option value="next_week">Next week</option>
                      </Select>
                      <button type="button" className="cx-btn cx-btn--sm" onClick={() => leave(i, 'done')} data-testid="inbox-done">Done</button>
                    </span>}
                    {view === 'done' && <button type="button" className="cx-link-btn" onClick={() => leave(i, 'reopen')}>Reopen</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : q.isSuccess && <Empty icon="owner-dashboard" title={view === 'open' ? 'Nothing is waiting on you' : view === 'snoozed' ? 'Nothing snoozed' : 'Nothing done yet'}>
          {view === 'open' ? 'Assignments, mentions, approvals, tickets and bookings land here the moment they happen.' : null}</Empty>}
      </section>

      {tasks.data && (
        <section className="cx-panel cx-section cx-rise" style={delay(3, STAGGER.tiles)}>
          <div className="cx-form-row" style={{ justifyContent: 'space-between', marginBottom: 10 }}>
            <span className="cx-eyebrow"><b>On your plate</b> — tasks</span>
            <button type="button" className="cx-btn cx-btn--sm" onClick={() => navigate({ to: '/ops' })}>Open Tasks</button>
          </div>
          {t.length ? (
            <table className="cx-table"><tbody>
              {[...overdue, ...dueToday, ...t.filter((x) => !overdue.includes(x) && !dueToday.includes(x))].slice(0, 8).map((x, n) => (
                <tr key={x.id} className="cx-rise" style={delay(n, STAGGER.list)}>
                  <td style={{ width: 14 }}><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: x.due_on && x.due_on < today ? 'var(--cx-fire)' : x.due_on === today ? 'var(--cx-warn)' : 'var(--cx-text-faint)' }} /></td>
                  <td style={{ fontWeight: 600 }}>{x.title}</td>
                  <td className="cx-faint cx-mono" style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{x.due_on ? (x.due_on < today ? `Overdue ${Math.round((Date.parse(today) - Date.parse(x.due_on)) / 86400000)}d` : x.due_on === today ? 'Due today' : `Due ${formatDay(`${x.due_on}T12:00:00`)}`) : 'No date'} · {x.list}</td>
                </tr>))}
            </tbody></table>
          ) : <p className="cx-none">No open tasks assigned to you.</p>}
          {t.length > 8 && <p className="cx-label" style={{ margin: '10px 0 0' }}>+{t.length - 8} more in Tasks</p>}
        </section>
      )}
    </div>
  );
}

function Health({ role }: { role: string }) {
  const q = useQuery({ queryKey: ['/api/ready'], queryFn: async () => (await fetch('/api/ready')).ok, refetchInterval: 30_000 });
  if (!['owner', 'admin'].includes(role)) return null;
  return <span className="cx-label" style={{ display: 'inline-flex', gap: 7, alignItems: 'center' }}><span className={`cx-chip cx-chip--dot cx-chip--${q.data ? 'good' : 'fire'}`} style={{ padding: 0, background: 'none' }} />Platform {q.data ? 'healthy' : 'unreachable'}</span>;
}

type Brief = { headline: string; body: string; counts: { fires?: number; decisions?: number; delegable?: number }; nextAction: { title: string; why: string; due: string } | null; unavailable: string[]; postedAt: string };
type Briefings = { morning: Brief | null; evening: Brief | null };

/** Scotty's brief for the owner (posted by the Paperclip agent on its `owner.briefing` key). After 3 pm the evening one, if it's in. */
function ScottyBrief({ b }: { b: Briefings | null }) {
  if (!b) return null;
  const evening = new Date().getHours() >= 15 && b.evening;
  const x = evening ? b.evening : b.morning ?? b.evening;
  if (!x) return null;
  const slot = x === b.evening ? 'Evening closeout' : 'Morning brief';
  const counts = ([['fires', 'Fires', 'fire'], ['decisions', 'Decisions', 'decision'], ['delegable', 'Can delegate', 'accent']] as const).filter(([k]) => (x.counts[k] ?? 0) > 0);
  return (
    <section className="cx-panel cx-section cx-rise" data-testid="scotty-brief">
      <div className="cx-form-row" style={{ justifyContent: 'space-between' }}>
        <span className="cx-eyebrow"><b>Scotty</b> — {slot} · {new Date(x.postedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</span>
        {counts.length > 0 && <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>{counts.map(([k, label, tone]) => <span key={k} className={`cx-chip cx-chip--${tone}`}>{label} {x.counts[k]}</span>)}</span>}
      </div>
      <h2 style={{ margin: '8px 0 6px', fontSize: 19 }}>{x.headline}</h2>
      <div className="cx-brief-body">{briefBody(x.body)}</div>
      {x.nextAction && (
        <div className="cx-inset" style={{ marginTop: 12, padding: '10px 14px' }}>
          <div className="cx-label">Next best action{x.nextAction.due ? ` · ${x.nextAction.due}` : ''}</div>
          <b style={{ display: 'block', marginTop: 2 }}>{x.nextAction.title}</b>
          {x.nextAction.why && <div className="cx-faint" style={{ fontSize: 12.5, marginTop: 2 }}>{x.nextAction.why}</div>}
        </div>
      )}
      {x.unavailable.length > 0 && <p className="cx-faint" style={{ margin: '10px 0 0', fontSize: 12.5 }}>Couldn’t read this time: {x.unavailable.join(', ')}.</p>}
    </section>
  );
}

/** The agent writes light markdown: "- " bullets, "#" headings and **bold**. Rendered as elements, never as HTML. */
function briefBody(md: string) {
  const inline = (t: string) => t.split(/(\*\*[^*]+\*\*)/g).map((p, i) => p.startsWith('**') && p.endsWith('**') ? <b key={i}>{p.slice(2, -2)}</b> : p);
  const out: ReactNode[] = [];
  let list: string[] = [];
  const flush = () => { if (list.length) out.push(<ul key={`u${out.length}`}>{list.map((l, i) => <li key={i}>{inline(l)}</li>)}</ul>); list = []; };
  for (const raw of md.split('\n')) {
    const line = raw.trim();
    if (/^[-*•] /.test(line)) { list.push(line.slice(2)); continue; }
    flush();
    if (!line) continue;
    if (line.startsWith('#')) out.push(<div key={`h${out.length}`} className="cx-label" style={{ marginTop: 10 }}>{line.replace(/^#+\s*/, '')}</div>);
    else out.push(<p key={`p${out.length}`}>{inline(line)}</p>);
  }
  flush();
  return out;
}
